import { Center, Text, Box, Image, Flex, Tooltip } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import Link from '_app/components/Link/Link'
import { useRouter } from '_app/hooks/useRouter'

import {
  ProductCustomOptions,
  ProductOption,
  ProductOptionKey,
} from '_app/types/product'

import { getEntries } from '_app/utils/getEntries'
import { APP_ROUTES } from '_app/routes'
import OptionHeader from './OptionHeader'
import SectionWithDivider from './SectionWithDivider'

interface ProductOptionsProps {
  options: ProductCustomOptions;
}

const getOptionValue = ({
  options,
  currentUrlKey,
  option,
}: {
  options: ProductCustomOptions;
  currentUrlKey?: string | string[];
  option: ProductOptionKey;
}): string =>
  options?.[option]?.find(({ url_key }) => url_key === currentUrlKey)?.value ??
  ''

const getImageSize = (key: ProductOptionKey): number => {
  switch (key) {
    case 'alfabet_swatch':
    case 'cyfry_swatch':
    case 'znaki_zodiaku_swatch':
      return 16
    default:
      return 8
  }
}

const ProductOptions = ({ options }: ProductOptionsProps) => {
  const {
    query: { url_key: currentUrlKey },
  } = useRouter()
  const { t } = useTranslation('product')

  const height = (id?: string) =>
    (typeof window !== 'undefined' &&
      id &&
      document?.getElementById(id)?.clientHeight) ??
    36

  const [optionsState, setOptionsState] = useState({
    alfabet_swatch: true,
    kolor3_swatch: false,
    cyfry_swatch: false,
    kolor_emalii_swatch: false,
    kolor_kamienia_swatch: false,
    material_swatch: false,
    rodzaj_kamienia_swatch: false,
    rozmiar_swatch: false,
    znaki_zodiaku_swatch: false,
  })

  const header: { [key in ProductOptionKey]: string } = {
    alfabet_swatch: t('ELEMENTS'),
    kolor3_swatch: t('COLOR'),
    cyfry_swatch: t('ELEMENTS'),
    kolor_emalii_swatch: t('COLOR'),
    kolor_kamienia_swatch: t('COLOR'),
    material_swatch: t('TYPE_OF_FINISH', { ns: 'common' }),
    rodzaj_kamienia_swatch: t('TYPE_OF_FINISH', { ns: 'common' }),
    rozmiar_swatch: t('SIZE', { ns: 'common' }),
    znaki_zodiaku_swatch: t('ELEMENTS'),
  }

  return (
    <>
      {getEntries<ProductOptionKey, ProductOption[]>(options)?.map(
        ([key, value]) =>
          value.length > 1 && (
            <SectionWithDivider key={key}>
              <Flex justifyContent="space-between">
                <OptionHeader
                  option={header[key]}
                  value={getOptionValue({
                    option: key,
                    options,
                    currentUrlKey,
                  })}
                />
                {key !== 'alfabet_swatch' &&
                  key !== 'znaki_zodiaku_swatch' &&
                  height(key) > 65 && (
                    <Text
                      fontSize={12}
                      cursor="pointer"
                      textDecoration="underline"
                      onClick={() =>
                        setOptionsState({
                          ...optionsState,
                          [key]: !optionsState[key],
                        })
                      }
                    >
                      {optionsState[key]
                        ? t('LESS_OPTIONS')
                        : t('MORE_OPTIONS')}
                    </Text>
                  )}
              </Flex>
              <Box
                overflowY={optionsState[key] ? 'visible' : 'hidden'}
                position="relative"
              >
                <Flex id={key} width="100%" flexWrap="wrap">
                  {value?.map(({ url_key, value, image }) => (
                    <Center
                      position="relative"
                      key={url_key}
                      _after={
                        url_key.indexOf('disabled') !== -1
                          ? {
                              content: '" "',
                              bg: '#fff',
                              opacity: 0.8,
                              position: 'absolute',
                              height: '100%',
                              width: '100%',
                            }
                          : {}
                      }
                    >
                      <Link
                        href={APP_ROUTES.PRODUCTS.BY_URL_KEY(
                          encodeURIComponent(url_key)
                        )}
                        passHref
                      >
                        {image ? (
                          <Box
                            height={getImageSize(key)}
                            width={getImageSize(key)}
                            cursor="pointer"
                            border="1px solid"
                            p={1.5}
                            borderColor={
                              url_key === currentUrlKey ? '#000' : '#fff'
                            }
                            overflow="hidden"
                          >
                            <Tooltip label={value}>
                              <Image
                                width="100%"
                                height="100%"
                                src={image}
                                alt={value || url_key}
                              />
                            </Tooltip>
                          </Box>
                        ) : (
                          <Center
                            px={2}
                            py={1}
                            cursor="pointer"
                            borderRadius={0}
                            borderWidth={1}
                            borderColor={
                              url_key === currentUrlKey ? '#000' : '#fff'
                            }
                          >
                            <Text>{value}</Text>
                          </Center>
                        )}
                      </Link>
                    </Center>
                  ))}
                </Flex>
              </Box>
            </SectionWithDivider>
          )
      )}
    </>
  )
}

export default ProductOptions
